import {useEffect, useMemo, useRef} from "react";
import {useLocation} from "react-router-dom";
import {isValidTextChunk, updateLastMessage} from "@breachrx/streaming";
import {setLastViewed as setLastViewedApi, stopMaestroRequest} from "~/src/api";
import {sendClientToolResult} from "../../../api/trustedAdvisor/clientTool";
import {useFeatureFlags} from "../../../context/FeatureFlagProvider";
import {useRexAI} from "../../../context/RexAIProvider";
import {
  buildAssistantStreamMessage,
  buildConfirmationErrorMessage,
  clearMessageActivity,
  removeActivityOnlyMessage,
} from "~/src/pages/RexAITrustedAdvisor/Context/streamMessageHelpers";
import {buildStreamRequest} from "~/src/pages/RexAITrustedAdvisor/Context/streamRequestHelpers";
import {isRestartRequired} from "~/src/pages/RexAITrustedAdvisor/Context/confirmationHelpers";
import {
  mergeStreamOutcomes,
  toUiMessages,
} from "~/src/pages/RexAITrustedAdvisor/Context/sessionMessageHelpers";
import {
  getStreamFailureMessage, isActionableStreamFailure,
} from "~/src/pages/RexAITrustedAdvisor/Context/streamErrorHelpers";
import {any, contains, isEmpty, isString} from "underscore";

const DEPARTURE_STOP_TIMEOUT_MS = 5000;

const useStreamHandler = (
  apiClient,
  {
    currentRequest,
    sessionId,
    skipSubscription,
    user,
    aiPermissions,
    trustedAdvisorModelProfile,
    responseWorkflowName,
    refs,
    setMessages,
    setError,
    setSessionId,
    setCurrentRequest,
    setIsThinking,
    setTokenCounts,
    setPendingConfirmation,
    fetchSessions,
    onRequestPrepared,
    onInputAccepted,
    onInputNotAccepted,
    onInputAlreadyAccepted,
    onDepartureRestored,
  },
) => {
  const {showDeveloper} = useFeatureFlags();
  const {
    messageContext,
    scopeContext,
    formToolsEnabled,
    allowedUiFormFields,
    allowedListTools,
    availableTabs,
    setPendingClientTool,
    onAgentWrite,
  } = useRexAI();
  const {pathname} = useLocation();
  const pathnameRef = useRef(pathname);
  const currentSubPageRef = useRef(scopeContext?.subPage);
  const isAgentNavigationPendingRef = useRef(false);
  const requestPathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    currentSubPageRef.current = scopeContext?.subPage;
  }, [scopeContext?.subPage]);

  useEffect(() => {
    if (isAgentNavigationPendingRef.current) {
      requestPathnameRef.current = pathname;
      isAgentNavigationPendingRef.current = false;
    }
  }, [pathname]);

  const fullMessageContext = useMemo(
    () =>
      messageContext ?
        {
          ...messageContext,
          scopeContext,
          availableTabs: messageContext.toolMode === "addForm" ? [] : availableTabs || [],
          sentFrom: pathname.startsWith("/rex-ai-maestro") ? "maestro" : "sidebar",
        } :
        {
          sentFrom: pathname.startsWith("/rex-ai-maestro") ? "maestro" : "sidebar",
        },
    [messageContext, scopeContext, availableTabs, pathname],
  );

  // Capture context/client at dispatch. Refreshing those objects must not abort
  // and resend the same request; the next request captures their latest values.
  useEffect(() => {
    if (!currentRequest || !user?.email || skipSubscription) {
      return;
    }

    const {
      stoppedRef,
      streamAbortControllerRef,
      sessionIdRef,
      fullResponseRef,
      fullThinkingRef,
      lastChunkIndexRef,
      currentAssistantMessageId,
    } = refs;
    const abortController = new AbortController();
    requestPathnameRef.current = pathnameRef.current;
    isAgentNavigationPendingRef.current = false;
    streamAbortControllerRef.current = abortController;
    stoppedRef.current = false;

    let terminalReceived = false;
    let departureStopSent = false;
    let departureStop;
    let departureRecoveryStarted = false;
    let streamSessionId = currentRequest.sessionId || sessionId;
    const stopOnDeparture = () => {
      if (terminalReceived || departureStopSent || stoppedRef.current ||
        abortController.signal.aborted || !currentRequest.messageId) {
        return;
      }
      departureStopSent = true;
      // Keep the unload request small. Without a confirmed visible snapshot the server
      // saves a stopped marker, not an unseen continuation. Any ECS task may receive it.
      departureStop = stopMaestroRequest(apiClient, {
        messageId: currentRequest.messageId,
        ...(streamSessionId && {sessionId: streamSessionId}),
        partialContent: "",
        ...(currentRequest.agentTaskId && {agentTaskId: currentRequest.agentTaskId}),
        ...(currentRequest.confirmationId && {confirmationId: currentRequest.confirmationId}),
      }, {keepalive: true, timeout: DEPARTURE_STOP_TIMEOUT_MS}).catch((error) => {
        console.warn("Could not confirm Stop while leaving Maestro:", error.name);
      });
      abortController.abort();
    };
    const recoverOnReturn = async (event) => {
      if (!event.persisted || !departureStopSent || departureRecoveryStarted ||
        streamAbortControllerRef.current !== abortController) {
        return;
      }
      departureRecoveryStarted = true;
      try {
        await onDepartureRestored?.({messageId: currentRequest.messageId,
          stopPromise: departureStop});
      } catch (error) {
        console.error("Failed to recover Maestro after returning to the page:", error);
      }
    };
    window.addEventListener("pagehide", stopOnDeparture);
    window.addEventListener("pageshow", recoverOnReturn);

    (async () => {
      let inputAccepted = false;
      try {
        const streamRequest = buildStreamRequest({
          currentRequest,
          sessionId,
          user,
          aiPermissions,
          trustedAdvisorModelProfile,
          responseWorkflowName,
          fullMessageContext,
          formToolsEnabled,
          allowedUiFormFields,
          allowedListTools,
        });
        onRequestPrepared?.(streamRequest);
        for await (const payload of apiClient.streamTrustedAdvisor(streamRequest, {
          signal: abortController.signal,
        })) {
          if (payload.sessionId) {
            streamSessionId = payload.sessionId;
          }
          if (payload.done) {
            terminalReceived = true;
          }
          if (abortController.signal.aborted) {
            break;
          }
          if (stoppedRef.current) {
            continue;
          }

          if (payload.outcomeMessages) {
            setMessages((previous) =>
              mergeStreamOutcomes(
                previous,
                toUiMessages(payload.outcomeMessages),
                payload.done && currentRequest.agentTaskId ? currentRequest.messageId : undefined,
              ),
            );
          }

          if (payload.keepalive) {
            continue;
          }

          if (payload.inputAccepted) {
            inputAccepted = true;
            onInputAccepted?.(currentRequest.messageId);
            if (payload.sessionId) {
              setSessionId(payload.sessionId);
              sessionIdRef.current = payload.sessionId;
            }
            continue;
          }

          if (payload.inputAlreadyAccepted) {
            inputAccepted = true;
            onInputAccepted?.(currentRequest.messageId);
            if (payload.sessionId) {
              setSessionId(payload.sessionId);
              sessionIdRef.current = payload.sessionId;
            }
            setCurrentRequest(null);
            setIsThinking(false);
            await onInputAlreadyAccepted?.(payload);
            continue;
          }

          if (payload.activity) {
            if (payload.messageId && payload.messageId !== currentAssistantMessageId.current) {
              clearMessageActivity(setMessages, currentAssistantMessageId.current);
              currentAssistantMessageId.current = payload.messageId;
              setMessages((prev) => [
                ...prev,
                buildAssistantStreamMessage(payload.messageId, {
                  activity: payload.activity,
                  ...(currentRequest.agentTaskId && {resumeRequestId: currentRequest.messageId}),
                  ...(payload.confirmationResult && {
                    confirmationResult: payload.confirmationResult,
                  }),
                }),
              ]);
              fullResponseRef.current = "";
              fullThinkingRef.current = "";
              lastChunkIndexRef.current = -1;
            } else if (currentAssistantMessageId.current) {
              updateLastMessage(
                setMessages,
                {
                  activity: payload.activity,
                  ...(payload.confirmationResult && {
                    confirmationResult: payload.confirmationResult,
                  }),
                },
                (msg) => msg?.messageId === currentAssistantMessageId.current,
              );
            }
            setIsThinking(false);
            continue;
          }

          if (payload.thinking) {
            if (showDeveloper) {
              if (payload.messageId && payload.messageId !== currentAssistantMessageId.current) {
                currentAssistantMessageId.current = payload.messageId;
                setIsThinking(false);
                setMessages((prev) => [
                  ...prev,
                  buildAssistantStreamMessage(payload.messageId),
                ]);
                fullResponseRef.current = "";
                fullThinkingRef.current = "";
                lastChunkIndexRef.current = -1;
              }
              if (currentAssistantMessageId.current) {
                fullThinkingRef.current += payload.thinking;
                updateLastMessage(
                  setMessages,
                  {thinking: fullThinkingRef.current},
                  (msg) => msg?.messageId === currentAssistantMessageId.current,
                );
              }
            }
            continue;
          }

          if (payload.error) {
            const userActionRequired = isActionableStreamFailure(payload);
            if (!userActionRequired) {
              console.error("Stream error:", payload.error);
            }
            if (payload.done) {
              if (!inputAccepted) {
                onInputNotAccepted?.(currentRequest.messageId);
              }
              if (payload.pendingWidget) {
                updateLastMessage(
                  setMessages,
                  {pendingWidget: payload.pendingWidget},
                  (msg) => msg?.messageId === currentAssistantMessageId.current,
                );
              }
              if (payload.outcomeMessages) {
                removeActivityOnlyMessage(setMessages, currentAssistantMessageId.current);
              }
              clearMessageActivity(setMessages, currentAssistantMessageId.current);
              if (isRestartRequired(payload)) {
                setPendingConfirmation(null);
              }
              setError(getStreamFailureMessage(payload));
              setCurrentRequest(null);
              setIsThinking(false);
            }
            continue;
          }

          if (payload.agentPending) {
            if (payload.pendingWidget) {
              updateLastMessage(
                setMessages,
                {pendingWidget: payload.pendingWidget},
                (msg) => msg?.messageId === currentAssistantMessageId.current,
              );
            }
            removeActivityOnlyMessage(setMessages, currentAssistantMessageId.current);
            clearMessageActivity(setMessages, currentAssistantMessageId.current);
            const hasSavedErrors = any(
              payload.outcomeMessages,
              (message) => !isEmpty(message.agentErrors),
            );
            if (!isEmpty(payload.agentErrors) && !hasSavedErrors) {
              setMessages((prev) => [
                ...prev,
                buildConfirmationErrorMessage(payload),
              ]);
            }
            setPendingConfirmation({
              taskId: payload.taskId,
              confirmationId: payload.confirmationId,
              confirmation: payload.confirmation,
            });
            if (payload.sessionId) {
              setSessionId(payload.sessionId);
              sessionIdRef.current = payload.sessionId;
            }
            setCurrentRequest(null);
            setIsThinking(false);
            continue;
          }

          if (payload.clientToolPending) {
            clearMessageActivity(setMessages, currentAssistantMessageId.current);
            if (payload.sessionId) {
              setSessionId(payload.sessionId);
              sessionIdRef.current = payload.sessionId;
            }
            if (!payload.clientTool || !payload.agentTaskId) {
              console.error("clientToolPending missing clientTool or agentTaskId", {
                agentTaskId: payload.agentTaskId,
                hasClientTool: !!payload.clientTool,
              });
              setCurrentRequest(null);
              setIsThinking(false);
              setError(
                "I couldn't get the information needed for this page action. Please try again.",
              );
              continue;
            }
            if (payload.text) {
              fullResponseRef.current += payload.text;
              updateLastMessage(setMessages, {text: fullResponseRef.current});
            }
            const {toolUseId, toolName, input} = payload.clientTool;
            const currentPathname = pathnameRef.current;
            const isNavigationTool = contains(["ui_navigate", "ui_navigate_tab"], toolName);
            if (isNavigationTool && !(
              toolName === "ui_navigate_tab" &&
              input?.destination === currentSubPageRef.current &&
              currentPathname.endsWith(`/${input.destination}`)
            )) {
              isAgentNavigationPendingRef.current = true;
            }
            if (!isNavigationTool && currentPathname !== requestPathnameRef.current) {
              try {
                await sendClientToolResult(apiClient, {
                  agentTaskId: payload.agentTaskId,
                  toolUseId,
                  result: {error: "User navigated away from the form — do not retry UI tools"},
                });
              } catch (error) {
                console.error("Failed to send navigated away result:", error);
              }
            } else {
              setPendingClientTool({
                toolName,
                input,
                agentTaskId: payload.agentTaskId,
                toolUseId,
                onAbandon: async () => {
                  try {
                    await sendClientToolResult(apiClient, {
                      agentTaskId: payload.agentTaskId,
                      toolUseId,
                      result: {
                        error: "User navigated away from the page — do not retry UI tools",
                      },
                    });
                  } catch (error) {
                    console.error("Failed to send navigated away result:", error);
                  }
                },
              });
            }
            continue;
          }

          if (
            payload.messageId &&
            payload.messageId !== currentAssistantMessageId.current &&
            !payload.outcomeMessages
          ) {
            clearMessageActivity(setMessages, currentAssistantMessageId.current);
            currentAssistantMessageId.current = payload.messageId;
            setIsThinking(false);
            setMessages((prev) => [
              ...prev,
              buildAssistantStreamMessage(payload.messageId),
            ]);
            fullResponseRef.current = "";
            fullThinkingRef.current = "";
            lastChunkIndexRef.current = -1;
          }

          if (isValidTextChunk(payload, lastChunkIndexRef.current) && !stoppedRef.current) {
            lastChunkIndexRef.current = payload.chunkIndex;
            fullResponseRef.current += payload.text;
            updateLastMessage(
              setMessages,
              {
                text: fullResponseRef.current,
                activity: undefined,
                ...(payload.confirmationResult && {
                  confirmationResult: payload.confirmationResult,
                }),
              },
              (msg) => msg?.messageId === currentAssistantMessageId.current,
            );
          }

          if (payload.sessionId) {
            setSessionId(payload.sessionId);
            sessionIdRef.current = payload.sessionId;
          }

          if (payload.done && !payload.clientToolPending) {
            if (payload.terminationReason === "aborted" && isString(payload.finalContent)) {
              fullResponseRef.current = payload.finalContent;
              updateLastMessage(
                setMessages,
                {
                  text: payload.finalContent,
                  aborted: !!payload.aborted,
                  abortedAt: payload.abortedAt,
                  parentMessageId: payload.parentMessageId,
                  terminationReason: payload.terminationReason,
                  activity: undefined,
                },
                (msg) => msg?.messageId === payload.messageId,
              );
            }
            clearMessageActivity(setMessages, currentAssistantMessageId.current);
            const handleDone = async () => {
              if (abortController.signal.aborted) {
                return;
              }
              if (payload.agentResult) {
                // Saved outcomes already contain the complete, combined operation history.
                if (!payload.outcomeMessages) {
                  updateLastMessage(
                    setMessages,
                    {agentResult: payload.agentResult},
                    (msg) => msg?.messageId === payload.messageId,
                  );
                }
                const hasMcpCall = any(
                  payload.agentResult?.operations,
                  (op) => op.tool && !op.tool.startsWith("ui_"),
                );
                if (hasMcpCall && onAgentWrite) {
                  onAgentWrite();
                }
              }
              if (payload.generatedDocuments?.length > 0) {
                updateLastMessage(
                  setMessages,
                  {generatedDocuments: payload.generatedDocuments},
                  (msg) => msg?.messageId === payload.messageId,
                );
              }
              if (payload.pendingWidget) {
                updateLastMessage(
                  setMessages,
                  {pendingWidget: payload.pendingWidget},
                  (msg) => msg?.messageId === payload.messageId,
                );
              }
              setCurrentRequest(null);
              lastChunkIndexRef.current = -1;
              currentAssistantMessageId.current = null;
              setTokenCounts((prev) => {
                const updatedTokenCounts = {
                  inputTokens: prev.inputTokens + (payload?.inputTokens ?? 0),
                  outputTokens: prev.outputTokens + (payload?.outputTokens ?? 0),
                  totalTokens: prev.totalTokens + (payload?.totalTokens ?? 0),
                };
                console.log({
                  sessionId: payload.sessionId,
                  ...updatedTokenCounts,
                  toolUse: payload?.toolUseCounts,
                  citations: payload?.citations,
                  summary: payload?.summaryInfo,
                  sessionName: payload?.sessionName,
                });
                return updatedTokenCounts;
              });

              if (payload.sessionId) {
                try {
                  await setLastViewedApi(apiClient, {
                    sessionId: payload.sessionId,
                    email: user.email,
                  });
                } catch (err) {
                  console.error("Failed to set lastViewed:", err);
                }
                fetchSessions();
              }
            };
            setTimeout(handleDone, 0);
          }
        }
      } catch (err) {
        if (abortController.signal.aborted) {
          return;
        }
        clearMessageActivity(setMessages, currentAssistantMessageId.current);
        if (err.name !== "AbortError") {
          if (!inputAccepted) {
            onInputNotAccepted?.(currentRequest.messageId);
          }
          console.error("Stream error:", err);
          setError("Failed to get response. Please try again.");
        }
        setCurrentRequest(null);
        setIsThinking(false);
      }
    })();

    return () => {
      window.removeEventListener("pagehide", stopOnDeparture);
      window.removeEventListener("pageshow", recoverOnReturn);
      stopOnDeparture();
      abortController.abort();
    };
  }, [currentRequest?.messageId, user?.email, skipSubscription]);
};

export default useStreamHandler;
